import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isMobile } from "react-device-detect";
import Modal from "../shared/ModalComponent";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "@hypexone/uicore";
import { FaChevronDown } from "react-icons/fa";
import UVGamesGif from "../../../assets/images/yuvi-games-images/uv_games.gif";
import { SVLS_API_CLIENT } from "../../../App.utils";
import Button from "../shared/Button/Button";

interface YuviGamesProps {
  // "floating" keeps the badge pinned above the footer, "footer" renders it as a footer tab
  variant?: "floating" | "footer";
}

const YuviGames: React.FC<YuviGamesProps> = ({ variant = "floating" }) => {
  const { langData } = useAppSelector((state) => state.cms);
  const loggedIn = useAppSelector((state) => state.auth.loggedIn);
  const [gameSrc, setGameSrc] = useState<string>("");
  const { pathname } = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [isGameModalOpen, setIsGameModalOpen] = useState(false);

  const navigate = useNavigate();
  const ref = useRef(null);
  const [showDialog, setShowDialog] = useState<boolean>(false);

  const getGameUrl = async (
    gameId: string,
    gameCode: string,
    provider: string,
    subProvider: string
  ) => {
    if (loggedIn) {
      setLoading(true);
      const claims = sessionStorage.getItem("jwt_token").split(".")[1];
      const userStatus = JSON.parse(window.atob(claims)).status;

      if (userStatus === 0 || userStatus === 3) {
        return navigate(`/home`);
      }

      let response;
      try {
        const reqBody = {
          gameId: gameId,
          providerName: provider,
          subProviderName: subProvider,
          platformId: isMobile ? "mobile" : "desktop",
          type: "MINI",
        };
        response = await SVLS_API_CLIENT.post(
          "/catalog/v2/live-casino/game-url",
          reqBody,
          {
            headers: {
              Authorization: sessionStorage.getItem("jwt_token"),
            },
          }
        );
      } catch (e) {
        console.log(e);
      }

      if (response?.data) {
        setGameSrc(response.data);
        setLoading(false);
      }
    } else {
      navigate(`/`);
    }
  };

  const YUVI_LOBBY_GAME = {
    gameId: "902000",
    gameCode: "YG-MLOBBY",
    provider: "RG",
    subProvider: "Yuvi Games Lobby",
  } as const;

  const setDialogShow = (show: boolean) => {
    setShowDialog(show);
  };

  const handleGameClick = (gameId: string) => {
    setSelectedGame(gameId);
    setIsGameModalOpen(true);
  };

  const handleCloseGameModal = () => {
    setIsGameModalOpen(false);
    setSelectedGame(null);
  };

  const isFooterVariant = variant === "footer";

  return (
    <>
      <div
        className={
          isFooterVariant
            ? "relative flex flex-col items-center justify-center h-full cursor-pointer"
            : "fixed bottom-[60px] right-2 h-fit"
        }
        onClick={() => {
          if (loggedIn) {
            ref.current?.classList.remove("hidden");
            const routerEl = document.getElementsByClassName(
              "router-ctn"
            )[0] as HTMLElement;
            if (routerEl) {
              routerEl.style.maxHeight = "calc(100% - 250px)";
              routerEl.style.overflowY = "auto";
            }
            void getGameUrl(
              YUVI_LOBBY_GAME.gameId,
              YUVI_LOBBY_GAME.gameCode,
              YUVI_LOBBY_GAME.provider,
              YUVI_LOBBY_GAME.subProvider
            );
          } else {
            setShowDialog(true);
          }
        }}
      >
        {isFooterVariant ? (
          <>
            {/* Footer bar arcs up around the badge */}
            <span
              aria-hidden="true"
              className="absolute -inset-x-[6px] bottom-0 -top-[12px] rounded-t-full bg-mobile-footer-background-color"
            />
            <img
              className="relative -top-[6px]"
              src={UVGamesGif}
              alt="Mini Games"
            />
          </>
        ) : (
          <img className=" h-[70px]" src={UVGamesGif} />
        )}
        {/* <img src={MiniGameGif} className="absolute" /> */}
      </div>
      {/* <img
        src={YuviGamesGif}
        alt="Yuvi Games"
        className={
          pathname?.includes("home") || pathname === "/"
            ? "fixed bottom-16 right-1 w-[60px] h-[110px]"
            : "fixed bottom-3 right-1 w-[60px] h-[110px]"
        }
      /> */}

      {createPortal(
        <div
          ref={ref}
          className="bg-[#132225] w-full fixed bottom-[50px] h-fit z-50 hidden yuvi-lobby-ctn left-0 right-0 mx-auto"
        >
        <div className="mx-auto h-[245px] w-[98%]">
          {/* Header */}
          <div
            className="top-2 right-3 absolute"
            onClick={() => {
              ref.current?.classList.add("hidden");
              const routerEl = document.getElementsByClassName(
                "router-ctn"
              )[0] as HTMLElement;
              if (routerEl) {
                routerEl.style.maxHeight = "100%";
                routerEl.style.overflowY = "auto";
              }
            }}
          >
            <FaChevronDown className="text-[#ae7526]" />
          </div>

          {/* Games Grid */}
          <div className="w-full h-full">
            <iframe
              src={gameSrc}
              title={langData?.["dc_game"]}
              allowFullScreen
              className="w-full h-full rounded-t-[10px]"
              sandbox="allow-same-origin allow-forms allow-scripts allow-top-navigation allow-popups"
            ></iframe>
          </div>
        </div>
        </div>,
        document.body
      )}

      {/* Notice Modal */}
      <Modal
        isOpen={showDialog}
        onClose={() => setDialogShow(false)}
        title={langData?.["notice"]}
      >
        <p className="mb-4">
          Access required for gameplay. Please log in to proceed.
        </p>
        <Button
          className="rounded py-2 px-6 text-white bg-slate-800"
          onClick={() => setDialogShow(false)}
        >
          OK
        </Button>
      </Modal>
    </>
  );
};

export default YuviGames;
